const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export interface PeriodRange {
  from: string;
  to: string;
  label: string;
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

export function todayIso(now = new Date()): string {
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

export function quarterContaining(iso: string): PeriodRange {
  const [year, month] = iso.split('-').map(Number);
  const quarter = Math.floor((month - 1) / 3);
  const startMonth = quarter * 3 + 1;
  const endMonth = startMonth + 2;
  const endDay = new Date(Date.UTC(year, endMonth, 0)).getUTCDate();
  return {
    from: `${year}-${pad(startMonth)}-01`,
    to: `${year}-${pad(endMonth)}-${pad(endDay)}`,
    label: `Q${quarter + 1} ${year}`,
  };
}

export function shiftQuarter(from: string, delta: number): PeriodRange {
  const [year, month] = from.split('-').map(Number);
  const anchor = new Date(Date.UTC(year, month - 1 + delta * 3, 1));
  return quarterContaining(anchor.toISOString().slice(0, 10));
}

export function shiftMonth(from: string, delta: number): PeriodRange {
  const [year, month] = from.split('-').map(Number);
  const anchor = new Date(Date.UTC(year, month - 1 + delta, 1));
  return monthContaining(anchor.toISOString().slice(0, 10));
}

export function periodKind(from: string, to: string): 'quarter' | 'month' | 'custom' {
  const quarter = quarterContaining(from);
  if (quarter.from === from && quarter.to === to) return 'quarter';
  const month = monthContaining(from);
  if (month.from === from && month.to === to) return 'month';
  return 'custom';
}

export function shiftPeriod(from: string, to: string, delta: number): PeriodRange {
  const kind = periodKind(from, to);
  if (kind === 'quarter') return shiftQuarter(from, delta);
  if (kind === 'month') return shiftMonth(from, delta);
  const [fromYear, fromMonth, fromDay] = from.split('-').map(Number);
  const [toYear, toMonth, toDay] = to.split('-').map(Number);
  const start = new Date(Date.UTC(fromYear, fromMonth - 1, fromDay));
  const end = new Date(Date.UTC(toYear, toMonth - 1, toDay));
  const length = Math.round((end.getTime() - start.getTime()) / 86400000) + 1;
  start.setUTCDate(start.getUTCDate() + delta * length);
  end.setUTCDate(end.getUTCDate() + delta * length);
  const nextFrom = start.toISOString().slice(0, 10);
  const nextTo = end.toISOString().slice(0, 10);
  return { from: nextFrom, to: nextTo, label: `${nextFrom} to ${nextTo}` };
}

export function monthContaining(iso: string): PeriodRange {
  const [year, month] = iso.split('-').map(Number);
  const endDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return {
    from: `${year}-${pad(month)}-01`,
    to: `${year}-${pad(month)}-${pad(endDay)}`,
    label: `${MONTHS[month - 1]} ${year}`,
  };
}
